import React, { useState, useEffect, useMemo } from "react";
import { FaWhatsapp, FaUsers, FaHashtag } from "react-icons/fa";
import { dangerMessage, successMessage, warningMessage } from "../../../../components/Toast/Toast";

const WhatsAppBulkForm = ({ setIsToggleViewWhatsApp, eventID, token }) => {
  const [message, setMessage] = useState("");
  const [selectedAudience, setSelectedAudience] = useState("all");
  const [manualNumbers, setManualNumbers] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [btnLoad, setBtnLoad] = useState(false);
  const [sendMode, setSendMode] = useState("audience"); // 'audience' | 'manual'

  // Audience options
  const audienceOptions = [
    { value: "all", label: "All Participants" },
    { value: "attendees", label: "Attendees" },
    { value: "speakers", label: "Speakers" },
    { value: "security", label: "Event Security" },
  ];

  // Fetch recipients when audience changes (only in audience mode)
  useEffect(() => {
    const fetchRecipients = async () => {
      if (sendMode !== "audience" || !selectedAudience) {
        setRecipients([]);
        return;
      }
      
      setLoadingRecipients(true);
      try {
        const endpoint = `${process.env.REACT_APP_API}attendees/${eventID}/with-phone-numbers?audience=${selectedAudience}`;
        const response = await fetch(endpoint, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (!response.ok) throw new Error("Failed to fetch");
        
        const data = await response.json();
        setRecipients(data.data || []);
        
        if (data.data?.length === 0) {
          warningMessage("No phone numbers found for this audience");
        } else {
          successMessage(`Found ${data.data.length} recipients`);
        }
      } catch (error) {
        console.error("Error:", error);
        dangerMessage("Failed to load recipients");
        setRecipients([]);
      } finally {
        setLoadingRecipients(false);
      }
    };
    
    fetchRecipients();
  }, [selectedAudience, sendMode, eventID, token]);

  // Parse manual numbers
  const parsedManualNumbers = useMemo(() => {
    if (sendMode !== "manual" || !manualNumbers.trim()) return [];
    
    return manualNumbers
      .split(/[,\n]/)
      .map(num => num.trim())
      .filter(num => num.length > 0)
      .map(num => {
        const cleaned = num.replace(/[\s\-\(\)\.]/g, "");
        if (cleaned.startsWith("0")) return "+27" + cleaned.substring(1);
        if (cleaned.startsWith("27")) return "+" + cleaned;
        if (cleaned.startsWith("+")) return cleaned;
        return "+27" + cleaned;
      })
      .filter(num => /^\+\d{10,15}$/.test(num));
  }, [manualNumbers, sendMode]);

  const activeRecipients = sendMode === "audience" ? recipients : parsedManualNumbers;

  const handleSendBulkWhatsApp = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      dangerMessage("Please enter a message");
      return;
    }
    
    if (activeRecipients.length === 0) {
      dangerMessage("No recipients found");
      return;
    }
    
    const confirmed = window.confirm(
      `Send to ${activeRecipients.length} recipients?`
    );
    
    if (!confirmed) return;
    
    setBtnLoad(true);
    let sentCount = 0;
    
    activeRecipients.forEach((recipient, index) => {
      setTimeout(() => {
        try {
          const phone = sendMode === "audience" 
            ? formatPhoneNumber(recipient.phoneNumber)
            : recipient;
          
          const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, "_blank");
          sentCount++;
        } catch (error) {
          console.error("Error:", error);
        }
        
        if (index === activeRecipients.length - 1) {
          setTimeout(() => {
            successMessage(`Sent to ${sentCount} recipients`);
            if (typeof setIsToggleViewWhatsApp === "function") {
  setIsToggleViewWhatsApp(false);
}
          }, 2000);
        }
      }, index * 1500);
    });
  };

  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
    if (cleaned.startsWith("0")) return "+27" + cleaned.substring(1);
    if (cleaned.startsWith("27")) return "+" + cleaned;
    if (cleaned.startsWith("+")) return cleaned;
    return "+27" + cleaned;
  };

  return (
    <div style={{ padding: "20px" }}>
      <h3 style={{ marginBottom: "20px", fontSize: "18px", fontWeight: "600" }}>
        Send WhatsApp
      </h3>

      <form onSubmit={handleSendBulkWhatsApp}>
        {/* Message Input */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
            Message<span className="text-danger">*</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your WhatsApp message here..."
            rows={5}
            style={{
              width: "100%",
              padding: "12px",
              border: "2px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "14px",
              resize: "vertical",
            }}
            required
          />
        </div>

        {/* MODE TOGGLE - THIS WAS MISSING! */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
            Send Mode
          </label>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={() => setSendMode("audience")}
              style={{
                flex: 1,
                padding: "12px",
                border: `2px solid ${sendMode === "audience" ? "#f68a20" : "#e5e7eb"}`,
                background: sendMode === "audience" ? "#fff7ed" : "white",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontWeight: sendMode === "audience" ? "600" : "400",
                color: sendMode === "audience" ? "#f68a20" : "#374151",
              }}
            >
              <FaUsers />
              Select Audience
            </button>
            <button
              type="button"
              onClick={() => setSendMode("manual")}
              style={{
                flex: 1,
                padding: "12px",
                border: `2px solid ${sendMode === "manual" ? "#f68a20" : "#e5e7eb"}`,
                background: sendMode === "manual" ? "#fff7ed" : "white",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontWeight: sendMode === "manual" ? "600" : "400",
                color: sendMode === "manual" ? "#f68a20" : "#374151",
              }}
            >
              <FaHashtag />
              Manual Entry
            </button>
          </div>
        </div>

        {/* AUDIENCE SELECTION - Only show in audience mode */}
        {sendMode === "audience" && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
              Select your audience<span className="text-danger">*</span>
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {audienceOptions.map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    border: `2px solid ${selectedAudience === option.value ? "#f68a20" : "#e5e7eb"}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: selectedAudience === option.value ? "#fff7ed" : "white",
                  }}
                >
                  <input
                    type="radio"
                    name="audience"
                    value={option.value}
                    checked={selectedAudience === option.value}
                    onChange={(e) => setSelectedAudience(e.target.value)}
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                  <span style={{ fontWeight: selectedAudience === option.value ? "600" : "400" }}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            
            {/* Loading/Recipients Info */}
            {loadingRecipients && (
              <div style={{ marginTop: "12px", color: "#f68a20", fontSize: "14px" }}>
                Loading recipients...
              </div>
            )}
            {!loadingRecipients && recipients.length > 0 && (
              <div style={{ 
                marginTop: "12px", 
                padding: "12px", 
                background: "#f0fdf4", 
                borderRadius: "6px",
                color: "#166534",
                fontSize: "14px"
              }}>
                ✓ {recipients.length} recipients found
              </div>
            )}
            {!loadingRecipients && recipients.length === 0 && selectedAudience && (
              <div style={{ 
                marginTop: "12px", 
                padding: "12px", 
                background: "#fef2f2", 
                borderRadius: "6px",
                color: "#991b1b",
                fontSize: "14px"
              }}>
                ⚠️ No phone numbers found
              </div>
            )}
          </div>
        )}

        {/* MANUAL ENTRY - Only show in manual mode */}
        {sendMode === "manual" && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
              Phone Numbers<span className="text-danger">*</span>
            </label>
            <textarea
              value={manualNumbers}
              onChange={(e) => setManualNumbers(e.target.value)}
              placeholder="+27712345678, +27812345679&#10;Or one per line..."
              rows={4}
              style={{
                width: "100%",
                padding: "12px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                resize: "vertical",
              }}
            />
            <small style={{ display: "block", marginTop: "6px", color: "#6b7280", fontSize: "12px" }}>
              Enter numbers separated by commas or new lines
            </small>
            {parsedManualNumbers.length > 0 && (
              <div style={{ 
                marginTop: "8px", 
                padding: "8px", 
                background: "#f0fdf4", 
                borderRadius: "6px",
                color: "#166534",
                fontSize: "13px"
              }}>
                ✓ {parsedManualNumbers.length} valid numbers
              </div>
            )}
          </div>
        )}

        {/* Send Button */}
        <button
          type="submit"
          disabled={btnLoad || activeRecipients.length === 0 || !message}
          style={{
            width: "100%",
            padding: "14px",
            background: activeRecipients.length > 0 && message ? "#f68a20" : "#cbd5e1",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: activeRecipients.length > 0 && message ? "pointer" : "not-allowed",
          }}
        >
          {btnLoad ? (
            <>Sending...</>
          ) : (
            <>Send WhatsApp ({activeRecipients.length})</>
          )}
        </button>
      </form>
    </div>
  );
};

export default WhatsAppBulkForm;